import { isClearingBankPayment } from '@/lib/payments/savedPaymentMethods';
import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { contactKeys } from '@/lib/queryKeys';
import type {
  ContactCommunicationChannel,
  ContactCommunicationItem,
  ContactEstimatesSectionItem,
  ContactInvoicesSectionItem,
  ContactJobsSectionItem,
  ContactPaginatedResponse,
  ContactPaymentsSectionItem,
} from '@/types/contact';

type PaginationParams = {
  page?: number;
  limit?: number;
};

type ContactSectionQueryOptions = {
  enabled?: boolean;
};

type ContactCommunicationsParams = PaginationParams & {
  channel?: ContactCommunicationChannel | 'ALL';
  dateFrom?: string;
  dateTo?: string;
};

type ContactPaymentsParams = PaginationParams & {
  status?: string;
};

function normalizePaginatedResponse<T>(
  raw: Record<string, unknown>,
  normalizeItem: (item: Record<string, unknown>) => T | null,
  fallbackLimit: number,
): ContactPaginatedResponse<T> {
  const items = Array.isArray(raw.data) ? raw.data : [];
  const data = items
    .map((item) =>
      item && typeof item === 'object' ? normalizeItem(item as Record<string, unknown>) : null,
    )
    .filter((item): item is T => item !== null);

  return {
    data,
    total: Number(raw.total ?? data.length),
    page: Number(raw.page ?? 1),
    limit: Number(raw.limit ?? fallbackLimit),
    totalPages: Number(raw.totalPages ?? 1),
    hasMore: Boolean(raw.hasMore),
  };
}

function normalizeContactCommunication(
  raw: Record<string, unknown>,
): ContactCommunicationItem | null {
  const id = raw.id ? String(raw.id) : null;
  const title = raw.title ? String(raw.title) : null;
  const occurredAt = raw.occurredAt ? String(raw.occurredAt) : null;
  const createdAt = raw.createdAt ? String(raw.createdAt) : null;
  if (!id || !title || !occurredAt || !createdAt) return null;

  return {
    id,
    eventType: raw.eventType ? String(raw.eventType) : 'contact.event',
    title,
    summary: raw.summary ? String(raw.summary) : null,
    channel: raw.channel ? String(raw.channel) : null,
    actorType: raw.actorType ? String(raw.actorType) : null,
    actorId: raw.actorId ? String(raw.actorId) : null,
    occurredAt,
    createdAt,
    payload:
      raw.payload && typeof raw.payload === 'object' && !Array.isArray(raw.payload)
        ? (raw.payload as Record<string, unknown>)
        : undefined,
  };
}

function normalizeContactJob(raw: Record<string, unknown>): ContactJobsSectionItem | null {
  const id = raw.id ? String(raw.id) : null;
  if (!id) return null;

  return {
    id,
    jobNumber: raw.jobNumber ? String(raw.jobNumber) : null,
    name: raw.name ? String(raw.name) : null,
    status: raw.status ? String(raw.status) : 'DRAFT',
    createdAt: raw.createdAt ? String(raw.createdAt) : new Date().toISOString(),
    scheduledStart: raw.scheduledStart ? String(raw.scheduledStart) : null,
    assignedTo: raw.assignedTo ? String(raw.assignedTo) : null,
  };
}

function normalizeContactEstimate(
  raw: Record<string, unknown>,
): ContactEstimatesSectionItem | null {
  const id = raw.id ? String(raw.id) : null;
  if (!id) return null;
  const metadata =
    raw.metadata && typeof raw.metadata === 'object' && !Array.isArray(raw.metadata)
      ? (raw.metadata as Record<string, unknown>)
      : {};

  return {
    id,
    estimateNumber: raw.estimateNumber ? String(raw.estimateNumber) : null,
    title: raw.title ? String(raw.title) : null,
    status: raw.status ? String(raw.status) : 'DRAFT',
    createdAt: raw.createdAt ? String(raw.createdAt) : new Date().toISOString(),
    grandTotal: Number(raw.grandTotal ?? 0),
    salespersonId: typeof metadata.salespersonId === 'string' ? metadata.salespersonId : null,
  };
}

function normalizeContactInvoice(raw: Record<string, unknown>): ContactInvoicesSectionItem | null {
  const id = raw.id ? String(raw.id) : null;
  if (!id) return null;
  const hasQbSync = Boolean(raw.qbInvoiceId);

  return {
    id,
    invoiceNumber: raw.invoiceNumber ? String(raw.invoiceNumber) : null,
    status: raw.status ? String(raw.status) : 'DRAFT',
    // The server's badge state; `computeInvoiceBadgeStatus` validates it.
    displayStatus: raw.displayStatus
      ? (String(raw.displayStatus) as ContactInvoicesSectionItem['displayStatus'])
      : undefined,
    createdAt: raw.createdAt ? String(raw.createdAt) : new Date().toISOString(),
    dueDate: raw.dueDate ? String(raw.dueDate) : null,
    total: Number(raw.total ?? 0),
    balanceDue: Number(raw.balanceDue ?? 0),
    amountPaid: Number(raw.amountPaid ?? 0),
    // Allowlist mapper: without these the list cannot net clearing money.
    clearingBankTotal: raw.clearingBankTotal == null ? null : Number(raw.clearingBankTotal),
    clearingBankCash: raw.clearingBankCash == null ? null : Number(raw.clearingBankCash),
    provisionalClearingTotal:
      raw.provisionalClearingTotal == null ? null : Number(raw.provisionalClearingTotal),
    clearingSince: raw.clearingSince ? String(raw.clearingSince) : null,
    jobType:
      raw.jobType && typeof raw.jobType === 'object'
        ? {
            id: String((raw.jobType as Record<string, unknown>).id ?? ''),
            name: String((raw.jobType as Record<string, unknown>).name ?? ''),
          }
        : null,
    qbInvoiceId: raw.qbInvoiceId ? String(raw.qbInvoiceId) : null,
    syncStatus: hasQbSync ? 'SYNCED' : 'NOT_SYNCED',
  };
}

function normalizeContactPayment(raw: Record<string, unknown>): ContactPaymentsSectionItem | null {
  const id = raw.id ? String(raw.id) : null;
  if (!id) return null;

  return {
    id,
    amount: Number(raw.amount ?? 0),
    status: raw.status ? String(raw.status) : 'PENDING',
    paymentMethod: raw.paymentMethod ? String(raw.paymentMethod) : 'OTHER',
    // Only beside a method the caller may see (see `paymentMethodLabel`).
    flow: raw.paymentMethod && raw.flow ? String(raw.flow) : null,
    contactId: raw.contactId ? String(raw.contactId) : null,
    invoiceId: raw.invoiceId ? String(raw.invoiceId) : null,
    invoiceNumber: raw.invoiceNumber ? String(raw.invoiceNumber) : null,
    processedAt: raw.processedAt ? String(raw.processedAt) : null,
    createdAt: raw.createdAt ? String(raw.createdAt) : new Date().toISOString(),
    processorId: raw.processorId ? String(raw.processorId) : null,
    gatewayProvider: raw.gatewayProvider ? String(raw.gatewayProvider) : null,
    gatewayTxnId: raw.gatewayTxnId ? String(raw.gatewayTxnId) : null,
    updatedBy: raw.updatedBy ? String(raw.updatedBy) : null,
    transactionId: raw.transactionId ? String(raw.transactionId) : null,
    reference: raw.reference ? String(raw.reference) : null,
    reversalOf: raw.reversalOf ? String(raw.reversalOf) : null,
    cancelledByStaffAt: raw.cancelledByStaffAt ? String(raw.cancelledByStaffAt) : null,
    syncStatus: raw.syncStatus === 'SYNCED' ? 'SYNCED' : 'NOT_SYNCED',
    qbPaymentId: raw.qbPaymentId ? String(raw.qbPaymentId) : null,
    cardBrand: raw.cardBrand ? String(raw.cardBrand) : null,
    cardLast4: raw.cardLast4 ? String(raw.cardLast4) : null,
    receiptId: raw.receiptId ? String(raw.receiptId) : null,
    receiptNumber: raw.receiptNumber ? String(raw.receiptNumber) : null,
  };
}

export function useContactCommunications(contactId: string, params: ContactCommunicationsParams) {
  const queryParams = {
    page: params.page ?? 1,
    limit: params.limit ?? 10,
    ...(params.channel && params.channel !== 'ALL' ? { channel: params.channel } : {}),
    ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
    ...(params.dateTo ? { dateTo: params.dateTo } : {}),
  };

  return useApiQuery<ContactPaginatedResponse<ContactCommunicationItem>>({
    queryKey: contactKeys.communications(contactId, queryParams),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        `/contacts/${contactId}/communications`,
        {
          params: queryParams,
        },
      );
      return normalizePaginatedResponse(raw, normalizeContactCommunication, queryParams.limit);
    },
    enabled: Boolean(contactId),
    showErrorToast: false,
  });
}

export function useContactJobsSection(contactId: string, params: PaginationParams) {
  const queryParams = {
    page: params.page ?? 1,
    limit: params.limit ?? 5,
  };

  return useApiQuery<ContactPaginatedResponse<ContactJobsSectionItem>>({
    queryKey: contactKeys.jobs(contactId, queryParams),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        `/contacts/${contactId}/jobs`,
        {
          params: queryParams,
        },
      );
      return normalizePaginatedResponse(raw, normalizeContactJob, queryParams.limit);
    },
    enabled: Boolean(contactId),
    showErrorToast: false,
  });
}

export function useContactEstimatesSection(contactId: string, params: PaginationParams) {
  const queryParams = {
    page: params.page ?? 1,
    limit: params.limit ?? 5,
  };

  return useApiQuery<ContactPaginatedResponse<ContactEstimatesSectionItem>>({
    queryKey: contactKeys.estimates(contactId, queryParams),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        `/contacts/${contactId}/estimates`,
        {
          params: queryParams,
        },
      );
      return normalizePaginatedResponse(raw, normalizeContactEstimate, queryParams.limit);
    },
    enabled: Boolean(contactId),
    showErrorToast: false,
  });
}

export function useContactInvoicesSection(
  contactId: string,
  params: PaginationParams,
  options: ContactSectionQueryOptions = {},
) {
  const queryParams = {
    page: params.page ?? 1,
    limit: params.limit ?? 5,
  };

  return useApiQuery<ContactPaginatedResponse<ContactInvoicesSectionItem>>({
    queryKey: contactKeys.invoices(contactId, queryParams),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        `/contacts/${contactId}/invoices`,
        {
          params: queryParams,
        },
      );
      return normalizePaginatedResponse(raw, normalizeContactInvoice, queryParams.limit);
    },
    enabled: Boolean(contactId) && (options.enabled ?? true),
    showErrorToast: false,
  });
}

export function useContactPaymentsSection(contactId: string, params: ContactPaymentsParams) {
  const queryParams = {
    page: params.page ?? 1,
    limit: params.limit ?? 5,
    ...(params.status ? { status: params.status } : {}),
  };

  return useApiQuery<ContactPaginatedResponse<ContactPaymentsSectionItem>>({
    queryKey: contactKeys.payments(contactId, queryParams),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        `/contacts/${contactId}/payments`,
        {
          params: queryParams,
        },
      );
      return normalizePaginatedResponse(raw, normalizeContactPayment, queryParams.limit);
    },
    enabled: Boolean(contactId),
    refetchInterval: (query) =>
      query.state.data?.data.some(isClearingBankPayment) ? 30_000 : false,
    showErrorToast: false,
  });
}
